import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useAuth } from 'context/user'
import { graphqlRequest } from 'lib/helpers'
import { YouTubeChatSession } from 'types/api'
import getYouTubeChatSessionsQuery from 'graphql/getYouTubeChatSessions.graphql'
import getEventYouTubeSessionsQuery from 'graphql/getEventYouTubeSessions.graphql'

export interface YouTubeChannelMeta { title: string | null, imageUrl: string | null }

export interface YouTubeSessionsStore {
  sessions: YouTubeChatSession[]
  loaded: boolean
  upsert: (row: YouTubeChatSession) => void
  meta: Record<string, YouTubeChannelMeta>
  rememberMeta: (channelId: string, meta: Partial<YouTubeChannelMeta>) => void
  runningCount: number
  refresh: () => Promise<void>
}

const rowKey = (row: YouTubeChatSession): string =>
  typeof row.channelId === 'string' && row.channelId !== ''
    ? `c:${row.channelId}`
    : `s:${row.sessionId}`

export const httpsImageOrNull = (raw: unknown): string | null =>
  typeof raw === 'string' && raw.startsWith('https://') ? raw : null

const nonEmptyOrNull = (raw: unknown): string | null =>
  typeof raw === 'string' && raw !== '' ? raw : null

const dedupeRows = (rows: YouTubeChatSession[]): YouTubeChatSession[] => {
  const byKey = new Map<string, YouTubeChatSession>()
  for (const row of rows) {
    const key = rowKey(row)
    const prev = byKey.get(key)
    if (prev === undefined) {
      byKey.set(key, row)
      continue
    }
    const prevRunning = prev.status === 'RUNNING'
    const rowRunning = row.status === 'RUNNING'
    if (rowRunning && !prevRunning) byKey.set(key, row)
    else if (rowRunning === prevRunning && row.startedAt > prev.startedAt) byKey.set(key, row)
  }
  return [...byKey.values()]
}

const foldMeta = (
  prev: Record<string, YouTubeChannelMeta>,
  channelId: string,
  title: string | null,
  imageUrl: string | null
): Record<string, YouTubeChannelMeta> => {
  if (channelId === '' || (title === null && imageUrl === null)) return prev
  const cur = prev[channelId]
  const nextTitle = title ?? cur?.title ?? null
  const nextImage = imageUrl ?? cur?.imageUrl ?? null
  if (cur !== undefined && cur.title === nextTitle && cur.imageUrl === nextImage) return prev
  return { ...prev, [channelId]: { title: nextTitle, imageUrl: nextImage } }
}

const foldRowsMeta = (
  prev: Record<string, YouTubeChannelMeta>,
  rows: YouTubeChatSession[]
): Record<string, YouTubeChannelMeta> => {
  let next = prev
  for (const row of rows) {
    const channelId = nonEmptyOrNull(row.channelId)
    if (channelId === null) continue
    next = foldMeta(next, channelId, nonEmptyOrNull(row.channelTitle), httpsImageOrNull(row.channelProfileImageUrl))
  }
  return next
}

interface StoreSnapshot {
  sessions: YouTubeChatSession[]
  loaded: boolean
  meta: Record<string, YouTubeChannelMeta>
}

interface StoreEntry {
  snapshot: StoreSnapshot
  listeners: Set<() => void>
  reading: Promise<void> | null
  touched: Set<string>
}

const createEntry = (): StoreEntry => ({
  snapshot: { sessions: [], loaded: false, meta: {} },
  listeners: new Set(),
  reading: null,
  touched: new Set()
})

const setSnapshot = (entry: StoreEntry, next: StoreSnapshot): void => {
  if (next === entry.snapshot) return
  entry.snapshot = next
  for (const listener of [...entry.listeners]) listener()
}

const upsertInto = (entry: StoreEntry, row: YouTubeChatSession): void => {
  const key = rowKey(row)
  if (entry.reading !== null) entry.touched.add(key)
  const prev = entry.snapshot
  const idx = prev.sessions.findIndex((r) => rowKey(r) === key)
  const sessions = idx === -1
    ? [...prev.sessions, row]
    : prev.sessions.map((r, i) => (i === idx ? row : r))
  setSnapshot(entry, { ...prev, sessions, meta: foldRowsMeta(prev.meta, [row]) })
}

const rememberInto = (entry: StoreEntry, channelId: string, next: Partial<YouTubeChannelMeta>): void => {
  const prev = entry.snapshot
  const meta = foldMeta(prev.meta, channelId, nonEmptyOrNull(next.title), httpsImageOrNull(next.imageUrl))
  if (meta !== prev.meta) setSnapshot(entry, { ...prev, meta })
}

const readInto = async (
  entry: StoreEntry,
  load: (token: string) => Promise<YouTubeChatSession[]>,
  token: string
): Promise<void> => {
  if (entry.reading !== null) return await entry.reading
  entry.touched = new Set()
  const run = async (): Promise<void> => {
    try {
      const rows = dedupeRows(await load(token))
      const prev = entry.snapshot
      const touched = entry.touched
      const kept = prev.sessions.filter((r) => touched.has(rowKey(r)))
      const fresh = rows.filter((r) => !touched.has(rowKey(r)))
      setSnapshot(entry, { ...prev, sessions: [...fresh, ...kept], meta: foldRowsMeta(prev.meta, rows) })
    } catch {
    } finally {
      entry.reading = null
      entry.touched = new Set()
      if (!entry.snapshot.loaded) setSnapshot(entry, { ...entry.snapshot, loaded: true })
    }
  }
  entry.reading = run()
  return await entry.reading
}

const refreshInto = async (
  entry: StoreEntry,
  load: (token: string) => Promise<YouTubeChatSession[]>,
  token: string
): Promise<void> => {
  if (entry.reading !== null) await entry.reading
  await readInto(entry, load, token)
}

const EVENT_ENTRY_LIMIT = 20
const eventEntries = new Map<string, StoreEntry>()
const eventEntryFor = (key: string): StoreEntry => {
  const found = eventEntries.get(key)
  if (found !== undefined) return found
  if (eventEntries.size >= EVENT_ENTRY_LIMIT) {
    for (const [k, e] of eventEntries) {
      if (e.listeners.size === 0 && e.reading === null) eventEntries.delete(k)
    }
  }
  const created = createEntry()
  eventEntries.set(key, created)
  return created
}

const useStoreEntry = (
  entry: StoreEntry,
  mountLoad: ((token: string) => Promise<YouTubeChatSession[]>) | null,
  refreshLoad: ((token: string) => Promise<YouTubeChatSession[]>) | null
): YouTubeSessionsStore => {
  const { auth0Token } = useAuth()
  const subscribe = useCallback((listener: () => void): (() => void) => {
    entry.listeners.add(listener)
    return () => { entry.listeners.delete(listener) }
  }, [entry])
  const getSnapshot = useCallback((): StoreSnapshot => entry.snapshot, [entry])
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    if (auth0Token === undefined || mountLoad === null) return
    void readInto(entry, mountLoad, auth0Token)
  }, [auth0Token, mountLoad, entry])

  const refresh = useCallback(async (): Promise<void> => {
    if (auth0Token === undefined || refreshLoad === null) return
    await refreshInto(entry, refreshLoad, auth0Token)
  }, [auth0Token, refreshLoad, entry])

  const upsert = useCallback((row: YouTubeChatSession): void => upsertInto(entry, row), [entry])
  const rememberMeta = useCallback(
    (channelId: string, next: Partial<YouTubeChannelMeta>): void => rememberInto(entry, channelId, next),
    [entry]
  )
  const runningCount = useMemo(
    () => snapshot.sessions.filter((s) => s.status === 'RUNNING' && s.foreignEvent !== true).length,
    [snapshot.sessions]
  )
  return { sessions: snapshot.sessions, loaded: snapshot.loaded, meta: snapshot.meta, upsert, rememberMeta, runningCount, refresh }
}

const loadAccountSessions = async (token: string): Promise<YouTubeChatSession[]> => {
  const resp = await graphqlRequest<{ sessions: YouTubeChatSession[] | null }>(
    getYouTubeChatSessionsQuery, {}, token
  )
  return resp.sessions ?? []
}

export const useAccountYouTubeSessions = (enabled: boolean): YouTubeSessionsStore => {
  const [entry] = useState(createEntry)
  const load = enabled ? loadAccountSessions : null
  return useStoreEntry(entry, load, load)
}

export const useEventYouTubeSessions = (eventId: string, enabled: boolean): YouTubeSessionsStore => {
  const { streamToolsUser } = useAuth()
  const userKey = streamToolsUser?.userId ?? ''
  const entry = useMemo(() => eventEntryFor(`${userKey}|${eventId}`), [userKey, eventId])
  const fetcher = useMemo(
    () => eventId !== ''
      ? async (token: string): Promise<YouTubeChatSession[]> => {
        const resp = await graphqlRequest<{ sessions: YouTubeChatSession[] | null }>(
          getEventYouTubeSessionsQuery, { eventId }, token
        )
        return resp.sessions ?? []
      }
      : null,
    [eventId]
  )
  return useStoreEntry(entry, enabled ? fetcher : null, fetcher)
}
